import { useCurrentAreaIfAvailable } from "@/modes/mode-data-context";
import { CadModelObject, PointCloudObject } from "@/object-cache";
import { useAppSelector } from "@/store/store-hooks";
import { selectIElementWorldTransform } from "@faro-lotv/app-component-toolbox";
import { GUID, IElementClippingBox } from "@faro-lotv/ielement-types";
import {
  isCadModel,
  LodPointCloud,
  OrientedBoundingBox,
} from "@faro-lotv/lotv";
import { selectMainAreaVolume } from "@faro-lotv/project-source";
import { useMemo } from "react";
import { Box3, Matrix4, Object3D, Quaternion, Vector3 } from "three";

// Type returned from useObjectLimitedBoundingBox
export type ObjectLimitedBoundingBox = {
  // object bounding box in world space; undefined if the cad is missing or has no geometry
  boundingBox?: Box3;
  // true if some nodes were too far and excluded from returned bounding box; false otherwise
  clippedBox: boolean;
};
/**
 * Compute a CadModelObject bounding box limited/clipped to a maximum size.
 * This value should be used instead of useObjectBoundingBox for CADs that contain outlier geometries
 * that would otherwise cause the view to be too larger to be practical.
 *
 * @param cadModel The CadModelObject object, or undefined if cadModel is null or empty
 * @param id The id of this object in the ielement project
 * @returns
 * - otherwise, a ObjectLimitedBoundingBox with:
 *   - boundingBox: node that are "too far" from first node will be excluded from the bounding box
 *     (too far mean the bounding sphere would have a radius more than 100 meters)
 *   - clippedBox: true if some nodes were too far and excluded from returned bounding box;
 *     false if useObjectLimitedBoundingBox returned same as useObjectBoundingBox
 */
export function useCadModelObjectLimitedBoundingBox(
  cadModel: CadModelObject | null,
  id?: GUID,
): ObjectLimitedBoundingBox {
  const { worldMatrix } = useAppSelector(selectIElementWorldTransform(id));

  const completeBBox = useObjectBoundingBox(cadModel, id);

  const cadBBox = useMemo((): ObjectLimitedBoundingBox => {
    // Maximum size of the bounding box used for cadBBox
    const LIMITED_BOUNDING_BOX_SIZE = 400;
    const limitedBBox = cadModel?.computeLimitedBoundingBox(
      LIMITED_BOUNDING_BOX_SIZE,
    );

    if (!limitedBBox || limitedBBox.boundingBox.isEmpty()) {
      // there might no node smaller than LIMITED_BOUNDING_BOX_SIZE; in this case, retreat to useObjectBoundingBox
      return {
        boundingBox: completeBBox,
        clippedBox: false,
      };
    }

    limitedBBox.boundingBox.applyMatrix4(new Matrix4().fromArray(worldMatrix));

    return {
      boundingBox: limitedBBox.boundingBox,
      clippedBox: limitedBBox.clippedBox,
    };
  }, [cadModel, worldMatrix, completeBBox]);

  return cadBBox;
}

/**
 * Compute a 3d object bounding box
 *
 * @param object3d The 3d object
 * @param id The id of this object in the ielement project
 * @returns The object bounding box in world space
 */
export function useObjectBoundingBox(
  object3d: Object3D | null,
  id?: GUID,
): Box3 | undefined {
  const { worldMatrix } = useAppSelector(selectIElementWorldTransform(id));

  const box = useMemo(() => {
    // 3D objects world pos may be invalid if they've never been rendered
    // So we can't expect it's worldMatrix to be correct
    // So we compute the box, then adjust using the world matrix from the ielement project
    const box = new Box3();
    if (object3d === null) return;

    // The LOD PointCloud has no geometry itself, so we cannot
    // expand the bounding box using the 3D object.
    if (object3d instanceof LodPointCloud) {
      box.expandByPoint(object3d.tree.boundingBox.min);
      box.expandByPoint(object3d.tree.boundingBox.max);
    } else if (isCadModel(object3d)) {
      box.union(object3d.boundingBox());
    } else {
      box.expandByObject(object3d);
      if (box.isEmpty()) {
        // cad might not be loaded yet; we should not return empty box in this case
        return;
      }
    }

    box.applyMatrix4(new Matrix4().fromArray(worldMatrix));
    return box;
  }, [object3d, worldMatrix]);

  return box;
}

/**
 *
 * @param pointCloud The current point cloud in the scene
 * @param cad The current CAD in the scene
 * @returns The cumulative bounding box of the given 3D models
 */
export function use3DmodelsBoundingBox(
  pointCloud: PointCloudObject | null,
  cad: CadModelObject | null | Error,
): Box3 {
  const cloudBox = useObjectBoundingBox(pointCloud, pointCloud?.iElement.id);

  const validCad = cad instanceof Error ? null : cad;
  const cadBox = useObjectBoundingBox(validCad, validCad?.iElement.id);

  return useMemo(() => {
    const box = new Box3();
    if (cloudBox) {
      box.union(cloudBox);
    }
    if (cadBox) {
      box.union(cadBox);
    }

    return box;
  }, [cadBox, cloudBox]);
}

/**
 * @returns the oriented bounding box for a ClippingBox iElement
 * @param clippingBox the element to get the bounding box for
 */
export function useIElementClippingBoxObb(
  clippingBox?: IElementClippingBox,
): OrientedBoundingBox | undefined {
  const worldTransform = useAppSelector(
    selectIElementWorldTransform(clippingBox?.id),
  );

  return useMemo(() => {
    if (!clippingBox) {
      return;
    }

    return {
      position: new Vector3().fromArray(worldTransform.position),
      quaternion: new Quaternion().fromArray(worldTransform.quaternion),
      size: new Vector3(
        clippingBox.size.x,
        clippingBox.size.y,
        clippingBox.size.z,
      ),
    };
  }, [clippingBox, worldTransform]);
}

/** @returns the clipping box for the current area's main volume, if available */
export function useCurrentAreaClippingBox(): OrientedBoundingBox | undefined {
  const currentArea = useCurrentAreaIfAvailable();

  const areaVolume = useAppSelector(selectMainAreaVolume(currentArea?.area));

  return useIElementClippingBoxObb(areaVolume);
}
